import React, { useCallback, useEffect, useState } from 'react'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import { useDispatch } from 'APP/Hooks/redux'
import styled from 'styled-components/native'
import { SafeAreaView } from 'react-native-safe-area-context'

import PatientActions from 'APP/Redux/PatientRedux'
import { handlePressContactSupport } from 'APP/Helpers/handlePressContactSupport'

import I18n, { isTranslationKey } from 'APP/I18n'
import Typography from 'APP/Converse/Typography'
import Button from 'APP/Converse/Button'
import LogoHeader from 'APP/Components/LogoHeader'
import Card from 'APP/Converse/Card'
import { Images } from 'APP/Themes'

export type VerifyNewEmailResultScreenProps = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  'verifyNewEmailResult'
>

const Container = styled(SafeAreaView)`
  flex: 1;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
  background-color: ${({ theme }) => theme.colors.background};
`

const ContentCard = styled(Card)`
  max-width: 550px;
  width: 100%;
  align-items: start;
  gap: ${({ theme }) => theme.metrics.baseMargin * 1.5}px;
`

const VerifyNewEmailResultScreen = ({
  route: {
    params: { errorType, success, email },
  },
}: VerifyNewEmailResultScreenProps) => {
  const [isLoading, setLoading] = useState(false)

  const dispatch = useDispatch()

  const messageKey = success ? 'success' : `error.${errorType}`
  const titleKey = `VerifyNewEmailScreen.${messageKey}.title`
  const logoSubtitle = `VerifyNewEmailScreen.${messageKey}.body1`
  const bodyKey = `VerifyNewEmailScreen.${messageKey}.body2`

  const onAuth = useCallback(() => {
    dispatch(PatientActions.runStartupAfterChangeEmail())
    setLoading(true)
  }, [dispatch])

  useEffect(() => {
    setLoading(false)
  }, [success])

  return (
    <Container>
      <ContentCard testID="VERIFY_NEW_EMAIL_RESULT_CONTENT_CARD">
        <LogoHeader
          source={Images.logoBlue}
          title={isTranslationKey(titleKey) ? I18n.t(titleKey) : undefined}
          subtitle={isTranslationKey(logoSubtitle) ? I18n.t(logoSubtitle, { email }) : undefined}
        />
        {!success && isTranslationKey(bodyKey) && <Typography>{I18n.t(bodyKey)}</Typography>}
        {success ? (
          <Button
            analyticsName="Submit VerifyNewEmailScreen"
            onPress={onAuth}
            title={I18n.t('VerifyNewEmailScreen.success.continue')}
            widthVariant="full"
            disabled={isLoading}
            isLoading={isLoading}
          />
        ) : (
          <Button
            analyticsName="Submit VerifyNewEmailScreen"
            onPress={handlePressContactSupport}
            title={I18n.t('VerifyNewEmailScreen.error.ctaContactUs')}
            widthVariant="full"
          />
        )}
      </ContentCard>
    </Container>
  )
}

export default VerifyNewEmailResultScreen
